import { useEffect, useRef } from 'react';

import {
  convertAppNodesToCanvasTokenNodes,
  useCanvasTokenNodeStore,
  useChatComposerStore,
  useChatHistoryNodeStore,
} from '@air/feature-ag-ui-chat';

import { useEditCanvasStore } from '../store';

/** Syncs edit-canvas nodes to the ag-ui-chat canvas token node store whenever they change. */
export const useCanvasTokenNodeSync = () => {
  const nodes = useEditCanvasStore((state) => state.nodes);
  const canvasId = useEditCanvasStore((state) => state.canvasId);
  const prevCanvasIdRef = useRef(canvasId);
  const isFirstSyncRef = useRef(true);

  useEffect(() => {
    const isFirstSync = isFirstSyncRef.current;
    isFirstSyncRef.current = false;
    const prevCanvasId = prevCanvasIdRef.current;
    prevCanvasIdRef.current = canvasId;
    // A draft committing (`''` → real id) is the same canvas; any other id change is a switch.
    const isCanvasSwitch = prevCanvasId !== canvasId && !(prevCanvasId === '' && canvasId);

    const converted = convertAppNodesToCanvasTokenNodes(nodes);
    const previous = useCanvasTokenNodeStore.getState().nodes;

    // Default updatedAt for nodes that lack a backend timestamp so they
    // sort correctly in the @-mention list. Inherits from the previous
    // store value (set on first appearance) so the timestamp is stable
    // across re-syncs. Replaced by the real API value once persisted.
    for (const [id, node] of Object.entries(converted)) {
      if (!node.updatedAt) {
        converted[id] = { ...node, updatedAt: previous[id]?.updatedAt ?? new Date().toISOString() };
      }
    }

    // Preserve removed nodes as inactive so chat [media:id] refs still resolve
    const removed: typeof previous = {};
    for (const [id, node] of Object.entries(previous)) {
      if (!converted[id]) {
        removed[id] = node;
      }
    }

    if (isCanvasSwitch || isFirstSync) {
      // Cross-boundary diffs aren't deletions: a fresh mount or a canvas switch swaps the whole
      // node set, and parking it would let another canvas's media resolve into this one's runs.
      // The host clears and re-hydrates inactiveNodes per canvas; a draft has no canvas detail,
      // so it clears here.
      if (!canvasId) {
        useChatHistoryNodeStore.getState().setInactiveNodes({});
      }
    } else if (Object.keys(removed).length > 0) {
      const { inactiveNodes } = useChatHistoryNodeStore.getState();
      useChatHistoryNodeStore.getState().setInactiveNodes({ ...inactiveNodes, ...removed });
    }

    // Remove composer tokens for deleted nodes and dissolved groups
    const removeToken = useChatComposerStore.getState().removeToken;

    if (removeToken) {
      // Remove tokens for individually deleted nodes
      for (const id of Object.keys(removed)) {
        removeToken(id);
      }

      // Remove group tokens when a multi-node group dissolves to ≤1 member
      const prevGroups = new Map<string, number>();
      for (const node of Object.values(previous)) {
        const gid = node.group?.id;

        if (gid) {
          prevGroups.set(gid, (prevGroups.get(gid) ?? 0) + 1);
        }
      }

      const nextGroups = new Map<string, number>();
      for (const node of Object.values(converted)) {
        const gid = node.group?.id;

        if (gid) {
          nextGroups.set(gid, (nextGroups.get(gid) ?? 0) + 1);
        }
      }

      for (const [groupId, count] of prevGroups) {
        if (count > 1 && (nextGroups.get(groupId) ?? 0) <= 1) {
          removeToken(groupId);
        }
      }
    }

    useCanvasTokenNodeStore.getState().setNodes(converted);
  }, [nodes, canvasId]);
};
